import React, { forwardRef, useState } from 'react';
import LoadingSpinner from './LoadingSpinner';

const DemoRequestModal = forwardRef(({ isOpen, onClose, onSubmit }, ref) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    companyType: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false); // Tracks if submission is in progress
  const [isSubmitted, setIsSubmitted] = useState(false); // Tracks if the form is successfully submitted

  if (!isOpen) return null; // Prevent rendering when modal is closed

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true); // Start submission

    try {
      await onSubmit(formData); // Call the parent handler to submit data
      setIsSubmitting(false); // Stop spinner
      setIsSubmitted(true); // Show success message
    } catch (error) {
      console.error('Submission failed:', error);
      setIsSubmitting(false); // Stop spinner if there's an error
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      companyType: '',
    });
    setIsSubmitted(false);
    setIsSubmitting(false);
    onClose(); // Close modal
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50">
      <div
        ref={ref}
        className="bg-white text-[#0A0F1F] p-8 rounded-lg shadow-2xl w-full max-w-3xl relative"
      >
        {/* Close Button */}
        <button
          onClick={resetForm}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-2xl"
        >
          ✖
        </button>

        {/* Modal Header */}
        <h2 className="text-3xl font-bold mb-6 text-center">
          {isSubmitted ? '🎉 Thank You!' : '🚀 Request a Demo'}
        </h2>

        {isSubmitted ? (
          /* Success Message */
          <div className="text-center">
            <p className="text-lg text-gray-700 mb-6">
              Dear <strong>{formData.firstName}</strong>, we have received your request.
            </p>
            <p className="text-gray-600 mb-8">
              An <strong>easyERP</strong> expert will contact you soon at{' '}
              <strong>{formData.email}</strong>.
            </p>
            <button
              onClick={resetForm}
              className="w-full bg-yellow-400 text-[#0A0F1F] font-bold py-3 rounded-md text-lg hover:bg-yellow-300 transition"
            >
              Close
            </button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-md"
                required
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-md"
                required
              />
              <input
                type="tel"
                name="phoneNumber"
                placeholder="Phone Number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-md"
              />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <input
                type="text"
                name="companyName"
                placeholder="Company Name"
                value={formData.companyName}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-md"
                required
              />
              <input
                type="text"
                name="companyType"
                placeholder="Company Type"
                value={formData.companyType}
                onChange={handleInputChange}
                className="w-full p-4 border border-gray-300 rounded-md"
              />
            </div>
            <button
              type="submit"
              className={`w-full font-bold py-3 rounded-md text-lg flex items-center justify-center gap-2 transition ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-yellow-400 hover:bg-yellow-300'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  Submitting <LoadingSpinner />
                </>
              ) : (
                'Submit'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
});

export default DemoRequestModal;
